import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Autocomplete,
  IconButton,
  Tooltip,
  Collapse,
  Skeleton,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Save as SaveIcon,
  Restore as RestoreIcon,
  ExpandMore as ExpandMoreIcon,
  Extension as ExtensionIcon,
  Close as CloseIcon,
  UnfoldMore as UnfoldMoreIcon,
  UnfoldLess as UnfoldLessIcon,
  DoneAll as DoneAllIcon,
  RemoveDone as RemoveDoneIcon,
  AccountTreeOutlined as DeptIcon,
  WarningAmberRounded as WarnIcon,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';

import {
  LoadRoles,
  getRoleComponentMatrix,
  getComponentPermissions,
  updateComponentPermissions,
  getDepartmentComponents,
  getAccessibleDepartments,
} from '../BaseURL';
import { getAllDepartments } from '../data/department';
import {
  ADMIN, BRAND,
  AdminPageHead, AdminCard, EmptyState, ToggleField,
  PrimaryButton, GhostButton, adminFieldSx,
} from './adminUi';

// ============================================================================
//  Component Access
// ============================================================================
//
//  Grants a role access to individual components (screens) within each of the
//  departments that role can already reach.
//
//  Two things were wrong with this screen.
//
//  SPEED. Picking a role built the view like this: fetch the role's accessible
//  departments, then loop over them and, inside the loop, `await` the
//  department's component list and `await` that role's permissions for it. The
//  awaits were inside the loop, so nothing overlapped — 1 + 2N round trips,
//  serialised. A role with twenty departments meant forty-one requests
//  end-to-end before the first checkbox rendered. It now takes one request
//  (`/role-matrix/:roleId`), which returns the same data from three queries.
//
//  On top of that, `hasAccess()` and `getPermissionStatus()` each ran a
//  console.log, and both are called from render for every component of every
//  department — several hundred console writes per render, every render. Those
//  are gone; console.log in a render path is genuinely slow, not just untidy.
//
//  SAVING. The old Save sent every permission it had in state, whether or not
//  it had been touched, and the server does a SELECT plus an INSERT/UPDATE/
//  DELETE for each one. Saving one checkbox rewrote the entire catalogue. The
//  screen now tracks which keys are actually dirty and sends only those, so a
//  save costs what the edit costs.
//
//  DESIGN. It was a stack of raw MUI Accordions under an unbounded full-width
//  <Select> that opened as a ~700px slab of role names across the whole page.
//  Now: searchable role picker, a live count of what is granted, and department
//  panels on the shared adminUi kit.

// `dirty` holds "<departmentId>::<componentName>" keys. Split on the FIRST
// separator only — a component name containing "::" would otherwise come back
// truncated and save against the wrong component.
const dirtyKey = (departmentId, componentName) => `${departmentId}::${componentName}`;
const splitKey = (key) => {
  const i = key.indexOf('::');
  return { departmentId: key.slice(0, i), componentName: key.slice(i + 2) };
};

const isGranted = (map, departmentId, componentName) =>
  map?.[departmentId]?.[componentName]?.access === true;

const ComponentAccess = () => {
  const [roles, setRoles]             = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState(null); // the role object

  const [departments, setDepartments]                 = useState([]);
  const [departmentComponents, setDepartmentComponents] = useState({});
  const [permissions, setPermissions]                 = useState({});

  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [loadError, setLoadError] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [expanded, setExpanded]       = useState(new Set());
  const [dirty, setDirty]             = useState(new Set());

  // The permissions exactly as loaded. Reset compares against this instead of
  // refetching, and it is what decides whether a toggle is a real change or a
  // toggle back to where it started.
  const baselineRef = useRef({});

  // Department names/descriptions are frontend data, not database rows.
  const allDepartments = useMemo(() => getAllDepartments(), []);

  useEffect(() => { loadRoles(); }, []);

  const loadRoles = async () => {
    try {
      setRolesLoading(true);
      const response = await axios.get(LoadRoles);
      if (response.data.success) setRoles(response.data.results || []);
    } catch (error) {
      console.error('Error loading roles:', error);
    } finally {
      setRolesLoading(false);
    }
  };

  // ── Loading a role's matrix ───────────────────────────────────────────────

  // Kept as a fallback for a server that does not have /role-matrix yet — the
  // frontend and backend deploy separately, and a 404 here would otherwise mean
  // a blank screen. Still parallel, unlike the original: the requests all go out
  // at once rather than one department at a time.
  const loadMatrixLegacy = useCallback(async (roleId) => {
    const deptRes = await axios.get(getAccessibleDepartments(roleId));
    if (!deptRes.data?.success) throw new Error('Failed to load accessible departments');

    const departmentIds = (deptRes.data.results || []).map((d) => String(d.department_id));

    const perDept = await Promise.all(
      departmentIds.map(async (id) => {
        const [comps, perms] = await Promise.all([
          axios.get(getDepartmentComponents(id)).catch(() => null),
          axios.get(getComponentPermissions(roleId, id)).catch(() => null),
        ]);
        return {
          id,
          components: comps?.data?.components || [],
          permissions: perms?.data?.permissions || {},
        };
      })
    );

    const components = {};
    const permissionsByDept = {};
    perDept.forEach((d) => {
      components[d.id] = d.components;
      permissionsByDept[d.id] = d.permissions;
    });

    return { departmentIds, components, permissions: permissionsByDept };
  }, []);

  const loadMatrix = useCallback(async (roleId) => {
    setLoading(true);
    setLoadError('');
    try {
      let data;
      try {
        const res = await axios.get(getRoleComponentMatrix(roleId));
        if (!res.data?.success) throw new Error(res.data?.message || 'Failed to load');
        data = res.data.results;
      } catch (err) {
        if (err.response?.status === 404) {
          data = await loadMatrixLegacy(roleId);
        } else {
          throw err;
        }
      }

      const ids = (data.departmentIds || []).map(String);
      // Compared as strings on both sides: the catalogue uses '1', '2', … and
      // the column has been seen holding both numbers and strings.
      const accessible = allDepartments.filter((d) => ids.includes(String(d.id)));

      setDepartments(accessible);
      setDepartmentComponents(data.components || {});
      setPermissions(data.permissions || {});
      baselineRef.current = JSON.parse(JSON.stringify(data.permissions || {}));
      setDirty(new Set());

      // Open the first department so the screen does not land fully collapsed
      // with no indication there is anything inside it.
      setExpanded(accessible.length ? new Set([accessible[0].id]) : new Set());
    } catch (error) {
      console.error('Error loading component permissions:', error);
      setDepartments([]);
      setDepartmentComponents({});
      setPermissions({});
      baselineRef.current = {};
      setLoadError(
        error.response?.data?.message || error.message || 'Could not load component access'
      );
    } finally {
      setLoading(false);
    }
  }, [allDepartments, loadMatrixLegacy]);

  const handleRoleChange = (role) => {
    setSelectedRole(role);
    setSearchQuery('');
    setExpanded(new Set());
    setDirty(new Set());
    if (role) {
      loadMatrix(role.id);
    } else {
      setDepartments([]);
      setDepartmentComponents({});
      setPermissions({});
      baselineRef.current = {};
    }
  };

  // ── Editing ───────────────────────────────────────────────────────────────

  const handlePermissionChange = useCallback((departmentId, componentName, checked) => {
    setPermissions((prev) => ({
      ...prev,
      [departmentId]: {
        ...(prev[departmentId] || {}),
        [componentName]: { access: checked },
      },
    }));

    setDirty((prev) => {
      const next = new Set(prev);
      const key = dirtyKey(departmentId, componentName);
      // Toggling back to the loaded value un-dirties the key, so Save stays
      // disabled when the net effect of the edits is nothing.
      if (isGranted(baselineRef.current, departmentId, componentName) === checked) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const setDepartmentAll = useCallback((departmentId, components, value) => {
    setPermissions((prev) => {
      const deptPerms = { ...(prev[departmentId] || {}) };
      components.forEach((c) => { deptPerms[c.component_name] = { access: value }; });
      return { ...prev, [departmentId]: deptPerms };
    });

    setDirty((prev) => {
      const next = new Set(prev);
      components.forEach((c) => {
        const key = dirtyKey(departmentId, c.component_name);
        if (isGranted(baselineRef.current, departmentId, c.component_name) === value) {
          next.delete(key);
        } else {
          next.add(key);
        }
      });
      return next;
    });
  }, []);

  const resetChanges = () => {
    setPermissions(JSON.parse(JSON.stringify(baselineRef.current)));
    setDirty(new Set());
  };

  const savePermissions = async () => {
    if (!selectedRole || dirty.size === 0) return;

    // Only what changed. The old version sent every permission in state on
    // every save, and the server runs a SELECT plus a write for each entry.
    const permissionsToUpdate = [...dirty].map((key) => {
      const { departmentId, componentName } = splitKey(key);
      return {
        roleId: parseInt(selectedRole.id, 10),
        departmentId: parseInt(departmentId, 10),
        componentName,
        permissions: JSON.stringify(permissions[departmentId]?.[componentName] || { access: false }),
      };
    });

    try {
      setSaving(true);
      const response = await axios.post(updateComponentPermissions, {
        permissions: permissionsToUpdate,
      });

      if (response.data.success) {
        baselineRef.current = JSON.parse(JSON.stringify(permissions));
        setDirty(new Set());
        await Swal.fire({
          title: 'Saved',
          text: `Component access updated for ${selectedRole.role}`,
          icon: 'success',
          timer: 2200,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      console.error('Error saving permissions:', error);
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed to update component access',
        icon: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  // ── Derived view ──────────────────────────────────────────────────────────

  const query = searchQuery.trim().toLowerCase();

  // One pass produces the filtered department list and its per-department
  // counts, so the render does not recompute them per row.
  const view = useMemo(() => {
    const rows = [];
    let totalComponents = 0;
    let totalGranted = 0;

    departments.forEach((dept) => {
      const all = departmentComponents[dept.id] || [];
      totalComponents += all.length;

      const grantedAll = all.filter((c) => isGranted(permissions, dept.id, c.component_name)).length;
      totalGranted += grantedAll;

      const deptMatches = !query || dept.name.toLowerCase().includes(query);
      // A department whose own name matches shows all of its components;
      // otherwise only the components that match are worth showing.
      const visible = deptMatches
        ? all
        : all.filter((c) =>
            c.component_name.toLowerCase().includes(query) ||
            String(c.description || '').toLowerCase().includes(query)
          );

      if (deptMatches || visible.length) {
        rows.push({ dept, all, visible, granted: grantedAll });
      }
    });

    return { rows, totalComponents, totalGranted };
  }, [departments, departmentComponents, permissions, query]);

  const toggleDepartment = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allExpanded = view.rows.length > 0 && view.rows.every((r) => expanded.has(r.dept.id));

  const roleOption = (role) => role?.role || '';

  return (
    <Box>
      <AdminPageHead
        icon={ExtensionIcon}
        title="Component Access"
        subtitle="Choose a role, then grant it the individual screens it needs inside each department it can already reach."
        actions={
          dirty.size > 0 ? (
            <>
              <GhostButton
                onClick={resetChanges}
                disabled={saving}
                startIcon={<RestoreIcon sx={{ fontSize: 17 }} />}
                // Ghost buttons are navy-on-white by default, which disappears
                // against the navy header band.
                sx={{ background: 'rgba(255,255,255,.94)', whiteSpace: 'nowrap' }}
              >
                Reset
              </GhostButton>
              <PrimaryButton
                onClick={savePermissions}
                disabled={saving}
                startIcon={saving
                  ? <CircularProgress size={15} sx={{ color: '#fff' }} />
                  : <SaveIcon sx={{ fontSize: 17 }} />}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {saving ? 'Saving…' : `Save ${dirty.size} change${dirty.size === 1 ? '' : 's'}`}
              </PrimaryButton>
            </>
          ) : null
        }
      />

      {/* ── Role picker ──────────────────────────────────────────────────── */}
      <AdminCard sx={{ mb: { xs: 1.75, sm: 2.5 } }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            gap: { xs: 2, md: 3 },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              component="label"
              htmlFor="role-picker"
              sx={{ display: 'block', mb: .875, fontSize: 12, fontWeight: 700, color: BRAND.ink }}
            >
              Role
            </Typography>

            {/* An Autocomplete, not a Select. A plain fullWidth <Select> with
                thirty-odd roles opens as a full-page-width slab tall enough to
                cover the entire screen, with no way to type. This filters as
                you type and is height-capped by the listbox below. */}
            <Autocomplete
              id="role-picker"
              options={roles}
              value={selectedRole}
              onChange={(e, v) => handleRoleChange(v)}
              getOptionLabel={roleOption}
              isOptionEqualToValue={(a, b) => a?.id === b?.id}
              loading={rolesLoading}
              disabled={rolesLoading}
              autoHighlight
              sx={{ maxWidth: { md: 460 } }}
              slotProps={{ paper: { sx: { borderRadius: ADMIN.radiusSm, mt: .5 } } }}
              ListboxProps={{ sx: { maxHeight: 320, fontSize: 13.5 } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={rolesLoading ? 'Loading roles…' : 'Search for a role…'}
                  sx={adminFieldSx}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 19, color: BRAND.inkFaint }} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Box>

          {/* Live figures. Without these the screen gives no sense of scale —
              you cannot tell a role with nothing granted from one still
              loading. */}
          {selectedRole && !loading && !loadError && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: 1,
                flexShrink: 0,
                minWidth: { md: 300 },
              }}
            >
              {[
                { label: 'Departments', value: departments.length, accent: BRAND.navy },
                { label: 'Components',  value: view.totalComponents, accent: BRAND.info },
                { label: 'Granted',     value: view.totalGranted, accent: BRAND.green },
              ].map((s) => (
                <Box
                  key={s.label}
                  sx={{
                    px: 1.25, py: 1, minWidth: 0,
                    borderRadius: ADMIN.radiusSm,
                    background: BRAND.canvas,
                    border: `1px solid ${BRAND.line}`,
                  }}
                >
                  <Typography sx={{ fontSize: 18, fontWeight: 800, lineHeight: 1.15, color: s.accent }}>
                    {s.value}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 9.5, fontWeight: 700, letterSpacing: '.4px',
                      textTransform: 'uppercase', color: BRAND.inkFaint,
                    }}
                  >
                    {s.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {dirty.size > 0 && (
          <Box
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.25,
              mt: 2, px: 1.5, py: 1.25,
              borderRadius: ADMIN.radiusSm,
              background: 'rgba(224,139,47,.09)',
              border: '1px solid rgba(224,139,47,.28)',
            }}
          >
            <WarnIcon sx={{ fontSize: 17, color: BRAND.warning, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 12.5, color: BRAND.ink, lineHeight: 1.55 }}>
              <b>{dirty.size}</b> unsaved change{dirty.size === 1 ? '' : 's'}. Nothing takes effect
              until you save.
            </Typography>
          </Box>
        )}
      </AdminCard>

      {/* ── Body ─────────────────────────────────────────────────────────── */}
      {!selectedRole ? (
        <AdminCard>
          <EmptyState
            icon={ExtensionIcon}
            title="Pick a role to begin"
            body="Component access is granted per role. Choose one above and its departments will appear here with everything you can switch on."
          />
        </AdminCard>
      ) : loading ? (
        <AdminCard>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" height={64} sx={{ borderRadius: ADMIN.radiusSm }} />
            ))}
          </Box>
        </AdminCard>
      ) : loadError ? (
        <AdminCard>
          <EmptyState
            icon={WarnIcon}
            title="Could not load component access"
            body={loadError}
            action={<PrimaryButton onClick={() => loadMatrix(selectedRole.id)}>Try again</PrimaryButton>}
          />
        </AdminCard>
      ) : departments.length === 0 ? (
        <AdminCard>
          <EmptyState
            icon={DeptIcon}
            title="This role cannot reach any department yet"
            body={`"${selectedRole.role}" has no department access, so there are no components to grant. Give it departments on the Department Access screen first.`}
          />
        </AdminCard>
      ) : (
        <AdminCard>
          {/* Toolbar */}
          <Box
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.25,
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
              pb: 1.75, mb: 2,
              borderBottom: `1px solid ${BRAND.line}`,
            }}
          >
            <TextField
              placeholder="Search departments or components…"
              value={searchQuery}
              size="small"
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ ...adminFieldSx, flex: 1, minWidth: { xs: '100%', sm: 220 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 19, color: BRAND.inkFaint }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery('')} aria-label="Clear search">
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />

            <Tooltip title={allExpanded ? 'Collapse all' : 'Expand all'}>
              <IconButton
                size="small"
                onClick={() =>
                  setExpanded(allExpanded ? new Set() : new Set(view.rows.map((r) => r.dept.id)))
                }
                sx={{ color: BRAND.inkMuted, flexShrink: 0, '&:hover': { background: BRAND.navySoft } }}
              >
                {allExpanded
                  ? <UnfoldLessIcon sx={{ fontSize: 19 }} />
                  : <UnfoldMoreIcon sx={{ fontSize: 19 }} />}
              </IconButton>
            </Tooltip>
          </Box>

          {view.rows.length === 0 ? (
            <EmptyState
              icon={SearchIcon}
              title="Nothing matches that search"
              body={`No department or component matches “${searchQuery}”.`}
              action={<GhostButton onClick={() => setSearchQuery('')}>Clear search</GhostButton>}
            />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {view.rows.map(({ dept, all, visible, granted }) => {
                // While searching, matching departments open themselves —
                // otherwise a hit inside a collapsed panel is invisible.
                const open = query ? true : expanded.has(dept.id);
                const allOn = all.length > 0 && granted === all.length;

                return (
                  <Box
                    key={dept.id}
                    sx={{
                      borderRadius: ADMIN.radiusSm,
                      border: `1px solid ${open ? BRAND.navyLight : BRAND.line}`,
                      overflow: 'hidden',
                      transition: 'border-color .15s ease',
                    }}
                  >
                    {/* Department header */}
                    <Box
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1.25,
                        px: { xs: 1.25, sm: 1.75 }, py: 1.25,
                        background: open ? BRAND.navySoft : BRAND.canvas,
                        cursor: query ? 'default' : 'pointer',
                        '&:hover': { background: BRAND.navySoft },
                      }}
                      onClick={query ? undefined : () => toggleDepartment(dept.id)}
                    >
                      {!query && (
                        <ExpandMoreIcon
                          sx={{
                            fontSize: 20, flexShrink: 0, color: BRAND.inkMuted,
                            transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
                            transition: 'transform .18s ease',
                          }}
                        />
                      )}

                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          sx={{ fontSize: 13.5, fontWeight: 700, color: BRAND.ink, lineHeight: 1.35 }}
                          noWrap
                        >
                          {dept.name}
                        </Typography>
                        <Typography sx={{ fontSize: 11.5, color: BRAND.inkFaint, lineHeight: 1.4 }} noWrap>
                          {all.length === 0
                            ? 'No components registered for this department'
                            : `${granted} of ${all.length} granted`}
                        </Typography>
                      </Box>

                      {/* Progress pill — the fastest read of where a department
                          stands without expanding it. */}
                      {all.length > 0 && (
                        <Typography
                          sx={{
                            flexShrink: 0, fontSize: 11, fontWeight: 800,
                            px: 1, py: .25, borderRadius: '20px',
                            background: granted === 0
                              ? 'rgba(15,31,61,.06)'
                              : allOn ? BRAND.green : `${BRAND.info}1F`,
                            color: granted === 0
                              ? BRAND.inkFaint
                              : allOn ? '#fff' : BRAND.info,
                          }}
                        >
                          {granted}/{all.length}
                        </Typography>
                      )}

                      {all.length > 0 && (
                        <Tooltip title={allOn ? 'Revoke all in this department' : 'Grant all in this department'}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              // The header itself toggles the panel.
                              e.stopPropagation();
                              setDepartmentAll(dept.id, all, !allOn);
                            }}
                            sx={{
                              flexShrink: 0,
                              color: allOn ? BRAND.danger : BRAND.green,
                              '&:hover': { background: allOn ? `${BRAND.danger}12` : `${BRAND.green}12` },
                            }}
                          >
                            {allOn
                              ? <RemoveDoneIcon sx={{ fontSize: 17 }} />
                              : <DoneAllIcon sx={{ fontSize: 17 }} />}
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>

                    <Collapse in={open} unmountOnExit>
                      <Box sx={{ px: { xs: 1.25, sm: 1.75 }, py: 1.75, background: BRAND.surface }}>
                        {visible.length === 0 ? (
                          <Typography sx={{ fontSize: 12.5, color: BRAND.inkFaint, py: 1 }}>
                            No components registered for this department.
                          </Typography>
                        ) : (
                          <Box
                            sx={{
                              display: 'grid',
                              // CSS Grid, not MUI <Grid>. The old code used
                              // `<Grid item xs={12}>`, whose props MUI 7 ignores
                              // outright — which is why every component was a
                              // full-width row no matter the screen size.
                              gap: 1.25,
                              gridTemplateColumns: {
                                xs: 'minmax(0, 1fr)',
                                md: 'repeat(2, minmax(0, 1fr))',
                                xl: 'repeat(3, minmax(0, 1fr))',
                              },
                            }}
                          >
                            {visible.map((component) => (
                              <ToggleField
                                key={component.component_name}
                                name={component.component_name}
                                label={component.component_name}
                                description={component.description}
                                checked={isGranted(permissions, dept.id, component.component_name)}
                                onChange={(e) =>
                                  handlePermissionChange(
                                    dept.id,
                                    component.component_name,
                                    e.target.checked
                                  )
                                }
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Collapse>
                  </Box>
                );
              })}
            </Box>
          )}
        </AdminCard>
      )}
    </Box>
  );
};

export default ComponentAccess;
